"use client";

import React, { useEffect, useState } from "react";
import { Image as AntImage } from "antd";
import Image from "next/image";
import { useRouter } from "next/navigation";
import type { PIMItem } from "@/types/pim";
import { clientEnv } from "../../../../env.client";
import { getCatalogState } from "@/types/catalog-state";
import ItemActions from "./ItemPage.ItemActions";
import { useSession } from "next-auth/react";
import { getAvailabilityByLocation, getItemName } from "@/lib/item-display";
import { formatAttributeValue } from "@/lib/attributeValue";
import { getAttributeDef, UNITS } from "@/lib/attributeCatalog";
import { usePriceMap, useRequestPrices } from "./PricingContext";

const ItemAvailability = ({ availability }: { availability: { location: string; quantity: number }[] }) => {
  return (
    <div className="flex flex-col gap-2">
      {availability.map((loc) => (
        <div key={loc.location} className="flex items-center gap-2">
          <svg className="w-4 h-5 shrink-0" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z"
              fill={loc.quantity > 0 ? "#22c55e" : "#9ca3af"}
            />
            <circle cx="12" cy="12" r="5" fill="white" />
          </svg>
          <span className="text-gray-700 font-medium">
            {loc.quantity > 0
              ? `${loc.quantity?.toLocaleString() || 0} available at ${loc.location}`
              : `Out of stock at ${loc.location}`}
          </span>
        </div>
      ))}
    </div>
  );
};

// Shown in place of the per-location availability when an item is reachable
// here but excluded from the catalog listing (which requires Active = true AND
// AvailableQty > 0). "Archived" is a neutral/inactive state; "Out of Stock" is
// a caution state for an active item with no stock.
const ItemStatusNotice = ({ status }: { status: "archived" | "out-of-stock" }) => {
  if (status === "archived") {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3">
        <svg className="w-6 h-6 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
        </svg>
        <div className="flex flex-col">
          <span className="text-base font-semibold text-gray-800">Archived</span>
          <span className="text-sm text-gray-500">This item is no longer part of our active inventory.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
      <svg className="w-6 h-6 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
      <div className="flex flex-col">
        <span className="text-base font-semibold text-amber-800">Out of Stock</span>
        <span className="text-sm text-amber-700">This item is currently unavailable.</span>
      </div>
    </div>
  );
};

interface ItemPageProps {
  item: PIMItem;
}

export default function ItemPage({ item }: ItemPageProps) {
  const router = useRouter();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageError, setImageError] = useState<Record<number, boolean>>({});

  const allMedia = item._IMAGES || [];
  const images = allMedia.filter((img) => !img.Url?.toLowerCase().endsWith(".pdf")).sort((a, b) => a.Priority - b.Priority);
  const documents = allMedia.filter((img) => img.Url?.toLowerCase().endsWith(".pdf"));
  const hasImages = images.length > 0 && !imageError[selectedImageIndex];
  const currentImage = images[selectedImageIndex];
  const [catalogState, setCatalogState] = useState<any>(null);

  // Derived, not state+effect — same reasoning as ItemCard: render the final
  // layout as soon as the session context has resolved, without an extra
  // post-mount flip. SSR/hydration both see false; no mismatch.
  const { data: session } = useSession();
  const isEmployee = !!session?.user?.name;

  // Pricing overlay (P3): the detail payload is price-free for everyone;
  // employees fetch the price client-side from the session-gated endpoint.
  const requestPrices = useRequestPrices();
  const priceMap = usePriceMap();
  useEffect(() => {
    if (isEmployee && item.Id != null) requestPrices([item.Id]);
  }, [isEmployee, item.Id, requestPrices]);
  // undefined = not fetched yet (dash), null = fetched but unpriced ($0).
  const retail = item.Id != null ? priceMap[item.Id] : undefined;

  useEffect(() => {
    setCatalogState(getCatalogState());
  }, []);

  // Extract meaningful attributes to display
  const attributes = item._ATTRIBUTES || {};
  const attributeEntries = Object.entries(attributes).filter(
    ([key, attr]) => {
      if (!(attr?.Name?.toLowerCase()?.includes("auction") || key?.toLowerCase()?.includes("auction")) && attr?.Value !== null && attr?.Value !== undefined && attr?.Value !== "" && attr.Name !== "New Image Needed") {
        return true;
      } else {
        return false;
      }
    });

  // Get condition badge color
  const getConditionColor = (condition: string) => {
    const lower = condition.toLowerCase();
    if (lower.includes("new") || lower.includes("excellent")) return "bg-green-100 text-green-800";
    if (lower.includes("good") || lower.includes("refurbished")) return "bg-blue-100 text-blue-800";
    if (lower.includes("fair") || lower.includes("used")) return "bg-yellow-100 text-yellow-800";
    return "bg-gray-100 text-gray-800";
  };

  const formatUSD = (amount: number) => {
    try {
      const n = typeof amount === "number" ? amount : Number(amount);
      const hasCents = Math.abs(n - Math.round(n)) > 1e-9;

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(n);
    } catch (error) {
      return "$0";
    }
  };

  const handleBackClick = () => {
    if (catalogState) {
      const url = new URL(catalogState.url);
      url.searchParams.set("restore", "true");
      url.searchParams.set("loaded", String(catalogState.itemsLoaded));
      router.push(url.toString());
    } else {
      const catalogState = getCatalogState();
      if (catalogState) {
        router.push(`${catalogState.url}?restore=true&loaded=${catalogState.itemsLoaded}`);
      } else {
        setCatalogState(null);
      }
    }
  };

  const condition = attributes["Condition"]?.Value as string;

  // Availability status. The catalog only lists items that are active AND in
  // stock (Active = true AND AvailableQty > 0); an item outside that set can
  // still be opened directly here, so we surface a clear status in place of the
  // per-location availability. Archived (inactive) wins over out-of-stock when
  // an item is both.
  const isArchived = item.Active === false;
  const isOutOfStock = !isArchived && !(typeof item.AvailableQty === "number" && item.AvailableQty > 0);

  const BackButton = () => (
    catalogState ? (
      <button
        onClick={handleBackClick}
        className="group hover:cursor-pointer flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200"
      >
        <svg
          className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-200"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span className="font-medium">Back to results</span>
      </button>
    ) : <div className="h-6 w-6"></div>
  );

  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-8">
        <div className="hidden md:flex mb-0 md:mb-6">
          <BackButton />
        </div>
        <div className={[
          "w-full",
          "grid",
          "gap-x-10",
          "gap-y-5",
          // desktop
          "grid-cols-[1fr_1fr]",
          "[grid-template-areas:'a_b''a_c']",
          // mobile
          "max-md:gap-x-1",
          "max-md:grid-cols-[1fr]",
          "max-md:[grid-template-areas:'b''a''c']",
        ].join(" ")}>
          {/* Image Gallery */}
          <div className="[grid-area:a] w-full space-y-8 pb-10">
            {/* Main Image */}
            <div className="aspect-square relative overflow-hidden bg-gray-100">
              {hasImages && currentImage ? (
                <AntImage.PreviewGroup
                  items={images.map((img) => (`${clientEnv.CDN_BASE_URL}/inventory/${img.Url}`))}
                >
                  <AntImage
                    src={`${clientEnv.CDN_BASE_URL}/inventory/${currentImage.Url}`}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                    srcSet={`
                      ${clientEnv.CDN_BASE_URL}/inventory/${currentImage.Url}?width=300 400w,
                      ${clientEnv.CDN_BASE_URL}/inventory/${currentImage.Url}?width=400 600w,
                      ${clientEnv.CDN_BASE_URL}/inventory/${currentImage.Url}?width=500 800w,
                    `}
                    alt={item.Name || "Product image"}
                    width="100%"
                    height="100%"
                    style={{ objectFit: "contain", position: "absolute", inset: 0 }}
                    onError={() => setImageError((prev) => ({ ...prev, [selectedImageIndex]: true }))}
                    preview={{
                      src: `${clientEnv.CDN_BASE_URL}/inventory/${currentImage.Url}`,
                      mask: "Click to view full size",
                      destroyOnClose: true
                    }}
                  />
                </AntImage.PreviewGroup>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg
                    className="w-24 h-24 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
            </div>


            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex flex-wrap gap-2 overflow-x-auto pb-2">
                {images.map((img, index) => {
                  return (<button
                    key={img.Url || index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`relative w-20 h-20 shrink-0 rounded-lg overflow-hidden border-2 transition-all duration-200 ${selectedImageIndex === index
                      ? "border-blue-500 shadow-md"
                      : "border-gray-200 hover:border-gray-400"
                      }`}
                  >
                    <Image
                      src={`${clientEnv.CDN_BASE_URL}/inventory/${img.Url}`}
                      alt={`${item.Name} - Document ${index + 1}`}
                      fill
                      sizes="100px"
                      className="object-cover"
                    />
                  </button>);
                })}
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="contents flex-col w-full md:w-1/2 space-y-5">

            {/* Title, condition, GPID */}
            <div className="[grid-area:b] w-full space-y-2 md:space-y-5">

              {/* Title and Condition */}
              <div>
                {condition ? (
                  <div className="flex items-center justify-around md:justify-start mb-3">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getConditionColor(
                        condition
                      )}`}
                    >
                      {capitalize(condition)}
                    </span>
                    <div className="flex md:hidden w-full items-center justify-end md:justify-start">
                      <BackButton />
                    </div>
                  </div>
                ) : (
                  <div className="flex md:hidden w-full items-center justify-end md:justify-start">
                    <BackButton />
                  </div>
                )}
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  {getItemName(item)}
                </h1>
              </div>

              <div className="flex flex-col gap-3">
                {/* SKU and Identifiers */}
                {item.GPID && (
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                    <span className="font-medium">GPID:</span> {item.GPID}
                  </div>
                )}

                {isArchived ? (
                  <ItemStatusNotice status="archived" />
                ) : isOutOfStock ? (
                  <ItemStatusNotice status="out-of-stock" />
                ) : (
                  <div className="flex flex-col gap-2 text-sm text-gray-500">
                    <span className="font-medium">Availability:</span>
                    <ItemAvailability availability={getAvailabilityByLocation(item)} />
                  </div>
                )}
              </div>

              {
                isEmployee ? (
                  <div className="flex border border-green-600 rounded-md flex-col 3xl:flex-row justify-start 3xl:justify-between items-start 3xl:items-center p-2 text-md">
                    <div className="flex items-center gap-3 w-full 3xl:w-auto">
                      <span className="text-gray-600">Unit price:</span>
                      <span className="font-semibold text-green-600 flex-1 flex justify-end">
                        {retail === undefined ? '—' : formatUSD(retail ?? 0)}
                      </span>
                    </div>
                    {item.AvailableQty && item.AvailableQty > 1 ? (
                      <div className="flex items-center gap-3 w-full 3xl:w-auto">
                        <span className="text-gray-600">Total value:</span>
                        <span className="font-semibold text-green-600 flex-1 flex justify-end">
                          {retail === undefined ? '—' : formatUSD((retail ?? 0) * (item.AvailableQty || 0))}
                        </span>
                      </div>
                    ) : <div className="flex flex-col 3xl:flex-row justify-start 3xl:justify-between items-start 3xl:items-center text-sm text-transparent">
                      <span>Total value:</span>
                    </div>}
                  </div>
                ) : null
              }

            </div>

            <div className="[grid-area:c] w-full space-y-6">
              <ItemActions item={item} />

              {/* Description */}
              {item.Description && (
                <div className="border-t pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Description</h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {item.Description}
                  </p>
                </div>
              )}

              {/* Attributes */}
              {attributeEntries.length > 0 && (
                <div className="border-t pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Specifications</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {attributeEntries.sort(([keyA], [keyB]) => keyA.localeCompare(keyB)).map(([key, attr]) => (
                      <div
                        key={key}
                        className="flex justify-between items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg"
                      >
                        <span className="text-gray-600 text-sm whitespace-nowrap">{attr.Name || key}</span>
                        <span className="text-gray-900 font-medium text-sm text-right">
                          {formatAttributeValue(attr, getAttributeDef(attr.Name || key), UNITS)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents Section */}
              {documents.length > 0 && (
                <div className="border-t pt-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Documents</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {documents.map((doc, index) => {
                      const thumbUrl = doc.Url.replace(/\.pdf$/i, "-thumb.jpg");
                      const fileName = doc.Url.split("/").pop()?.replace(".pdf", "") || `Document ${index + 1}`;
                      return (
                        <a
                          key={doc.Id || index}
                          href={`${clientEnv.CDN_BASE_URL}/inventory/pdf/${doc.Url}`}
                          target="_blank"
                          rel="external nofollow noopener noreferrer"
                          className="group block bg-gray-50 rounded-lg overflow-hidden border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all duration-200"
                        >
                          <div className="aspect-4/3 relative bg-gray-100">
                            <Image
                              src={`${clientEnv.CDN_BASE_URL}/inventory/pdf/${thumbUrl}?width=200`}
                              alt={fileName}
                              fill
                              sizes="150px"
                              className="object-cover"
                              unoptimized={process.env.NODE_ENV !== "production"}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 flex items-center justify-center">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 rounded-full p-2">
                                <svg
                                  className="w-5 h-5 text-blue-600"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="p-2 flex items-center gap-2">
                            <svg
                              className="w-4 h-4 text-red-500 shrink-0"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zm-3 9.5c0 .28-.22.5-.5.5h-2c-.28 0-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5h2c.28 0 .5.22.5.5v3zm4 0c0 .28-.22.5-.5.5h-2c-.28 0-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5h2c.28 0 .5.22.5.5v3zm4 0c0 .28-.22.5-.5.5h-2c-.28 0-.5-.22-.5-.5v-3c0-.28.22-.5.5-.5h2c.28 0 .5.22.5.5v3z" />
                            </svg>
                            <span className="text-xs text-gray-600 truncate font-medium">
                              {fileName}
                            </span>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {
          catalogState ? (
            <div className="flex md:hidden w-full items-center justify-end md:justify-start mt-8 -mb-3">
              <BackButton />
            </div>
          ) : <div className="h-6 w-6 mb-6"></div>
        }
      </div>
    </div>
  );
}
